import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../../mutation-types';
import CampaignBatchesAPI from '../../../api/crm/campaignBatches';

export const state = {
    records: [],
    contacts: [],
    uiFlags: {
        isFetching: false,
        isCreating: false,
        isUpdating: false,
        isDeleting: false,
        isPopulating: false,
        isImporting: false,
        isSending: false,
        isFetchingContacts: false,
    },
};

export const getters = {
    getUIFlags(_state) {
        return _state.uiFlags;
    },
    getBatches(_state) {
        return _state.records;
    },
    getBatchById: _state => id => {
        return _state.records.find(batch => batch.id === Number(id));
    },
    getBatchContacts(_state) {
        return _state.contacts;
    },
};

export const actions = {
    get: async function getBatches({ commit }, params = {}) {
        commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isFetching: true });
        try {
            const response = await CampaignBatchesAPI.get(params);
            const data = response.data.data || [];
            commit(types.SET_CAMPAIGN_BATCHES, data);
        } catch (error) {
            // Ignore error
        } finally {
            commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isFetching: false });
        }
    },

    show: async function showBatch({ commit }, id) {
        commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isFetching: true });
        try {
            const response = await CampaignBatchesAPI.show(id);
            commit(types.EDIT_CAMPAIGN_BATCH, response.data);
            return response.data;
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isFetching: false });
        }
    },

    create: async function createBatch({ commit }, batchObj) {
        commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isCreating: true });
        try {
            const response = await CampaignBatchesAPI.create(batchObj);
            commit(types.ADD_CAMPAIGN_BATCH, response.data);
            return response.data;
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isCreating: false });
        }
    },

    update: async function updateBatch({ commit }, { id, ...updateObj }) {
        commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isUpdating: true });
        try {
            const response = await CampaignBatchesAPI.update(id, updateObj);
            commit(types.EDIT_CAMPAIGN_BATCH, response.data);
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isUpdating: false });
        }
    },

    delete: async function deleteBatch({ commit }, id) {
        commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isDeleting: true });
        try {
            await CampaignBatchesAPI.delete(id);
            commit(types.DELETE_CAMPAIGN_BATCH, id);
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isDeleting: false });
        }
    },

    populateFromDeals: async function populateFromDeals(
        { commit },
        { batchId, filters }
    ) {
        commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isPopulating: true });
        try {
            const response = await CampaignBatchesAPI.populateFromDeals(
                batchId,
                filters
            );
            return response.data;
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isPopulating: false });
        }
    },

    importCsv: async function importCsv(
        { commit },
        { batchId, file, nameColumn, phoneColumn }
    ) {
        commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isImporting: true });
        try {
            const response = await CampaignBatchesAPI.importCsv(
                batchId,
                file,
                nameColumn,
                phoneColumn
            );
            return response.data;
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isImporting: false });
        }
    },

    fetchContacts: async function fetchContacts(
        { commit },
        { batchId, page = 1 }
    ) {
        commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isFetchingContacts: true });
        try {
            const response = await CampaignBatchesAPI.getContacts(batchId, page);
            commit(types.SET_CAMPAIGN_BATCH_CONTACTS, response.data.contacts || []);
            return response.data;
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isFetchingContacts: false });
        }
    },

    removeContact: async function removeContact(
        { commit, dispatch },
        { batchId, contactId }
    ) {
        try {
            await CampaignBatchesAPI.removeContact(batchId, contactId);
            // Refresh contacts list
            dispatch('fetchContacts', { batchId });
        } catch (error) {
            throw new Error(error);
        }
    },

    sendBatch: async function sendBatch({ commit }, batchId) {
        commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isSending: true });
        try {
            const response = await CampaignBatchesAPI.sendBatch(batchId);
            return response.data;
        } catch (error) {
            throw new Error(error);
        } finally {
            commit(types.SET_CAMPAIGN_BATCHES_UI_FLAG, { isSending: false });
        }
    },
};

export const mutations = {
    [types.SET_CAMPAIGN_BATCHES_UI_FLAG](_state, data) {
        _state.uiFlags = {
            ..._state.uiFlags,
            ...data,
        };
    },

    [types.ADD_CAMPAIGN_BATCH]: MutationHelpers.create,
    [types.SET_CAMPAIGN_BATCHES]: MutationHelpers.set,
    [types.EDIT_CAMPAIGN_BATCH]: MutationHelpers.update,
    [types.DELETE_CAMPAIGN_BATCH]: MutationHelpers.destroy,
    [types.SET_CAMPAIGN_BATCH_CONTACTS](_state, contacts) {
        _state.contacts = contacts;
    },
};

export default {
    namespaced: true,
    actions,
    state,
    getters,
    mutations,
};
